import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { ImageDataDb } from '../../../_shared/data/image-data-db.data';
import { TrackingEvents } from '../../../_shared/data/tracking-events.data';
import { ICard, ICardSlider, IDynamicElement } from '../../../_shared/models';
import { GtmService } from '../../../_shared/services/gtm.service';
import { LinkService } from '../../../_shared/services/link.service';
import { withConfigFallback } from '../../../_shared/utils/with-config-fallback';
import { SliderComponent } from '../../../slider/slider.component';
import { CardElementDirective } from '../../card-element.directive';
import { CardCollectionTextComponent } from '../card-collection-text/card-collection-text.component';

const SMALL_CARD_MIN_ITEMS = 4;

enum ScrollDirection {
    Next,
    Previous,
}

@Component({
    selector: 'sirius-card-slider',
    templateUrl: './card-slider.component.html',
    styleUrls: ['./card-slider.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CardCollectionTextComponent, SliderComponent, CardElementDirective],
})
export class CardSliderComponent implements IDynamicElement<ICardSlider> {
    private readonly gtmService = inject(GtmService);
    private readonly linkService = inject(LinkService);

    readonly config = input<ICardSlider | undefined>(undefined);
    readonly title = input('');
    readonly textHtml = input('');
    readonly items = input<ICard[]>([]);

    protected readonly resolvedTitle = withConfigFallback<ICardSlider, 'title', string>(
        this.config,
        'title',
        this.title
    );
    protected readonly resolvedTextHtml = withConfigFallback<ICardSlider, 'textHtml', string>(
        this.config,
        'textHtml',
        this.textHtml
    );
    protected readonly resolvedItems = withConfigFallback<ICardSlider, 'items', ICard[]>(
        this.config,
        'items',
        this.items
    );
    protected readonly cardSize = computed(() =>
        this.resolvedItems().length >= SMALL_CARD_MIN_ITEMS ? 'small' : 'medium'
    );
    protected readonly imageData = computed(() => ImageDataDb.card[this.cardSize()]);

    protected onSelect(index: number): void {
        const item = this.resolvedItems()[index];
        if (!item) return;

        const cfg = {
            ...item.link.tracking?.parameters,
            index,
            link_text: item.link.title,
            link_url: this.linkService.getFullPath(item.link.href),
        };

        this.gtmService.track(TrackingEvents.gtm['carouselSelect']([cfg]));
    }

    protected onScrollNext(): void {
        this.trackScroll(ScrollDirection.Next);
    }

    protected onScrollPrevious(): void {
        this.trackScroll(ScrollDirection.Previous);
    }

    private trackScroll(direction: ScrollDirection): void {
        const trackingEvent =
            direction === ScrollDirection.Next
                ? TrackingEvents.gtm['carouselNext']
                : TrackingEvents.gtm['carouselPrevious'];

        const cfg = {
            ...this.config()?.tracking?.parameters,
        };

        this.gtmService.track(trackingEvent([cfg]));
    }
}
